import React, {useContext, useMemo} from 'react'
import PropTypes from 'prop-types'
import {BuilderBreadcrumb} from '../breadcrumb'
import {BuilderContext} from '@builder.io/sdk-react'
import {useLocation} from 'react-router-dom'
import {articlesPageData, learningCenterArticlePageTypes, addStaticCrumbs} from './utils'
import {useFetchOneEntry} from '../../../../hooks/useFetchOneEntry'
import builderConfig from 'hec/builder/model-map'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {generateBreadcrumbLinkContent} from '../utils'

export const LearningCenterArticleBreadcrumb = (props) => {
    const {separator, builderBlock, attributes} = props
    const config = getConfig()
    const location = useLocation()
    const urlPaths = location.pathname.split('/').filter((path) => !!path)
    const lastPathName = urlPaths ? urlPaths[urlPaths.length - 1] : null

    const isArticleTopicPage = lastPathName === 'articles'
    const isArticlePage = lastPathName && !isArticleTopicPage && urlPaths?.includes('articles')
    const articleTopic = new URLSearchParams(location.search).get('topic')

    const builderContext = useContext(BuilderContext)
    const pageData = builderContext?.content?.data

    let pageType
    if (isArticlePage) {
        pageType = learningCenterArticlePageTypes.ARTICLE
    } else if (isArticleTopicPage) {
        pageType = learningCenterArticlePageTypes.ARTICLE_TOPIC
    }

    const {
        data: articleTopicData,
        isLoading: articleTopicDataLoading,
        error: articleTopicDataError
    } = useFetchOneEntry({
        queryKey: ['Builder-Fetch-Learning-Center', articleTopic],
        options: {
            model: builderConfig.learningCenterArticleTopic,
            apiKey: config.app.builder.api,
            enrich: false,
            query: {
                name: articleTopic
            },
            fields: 'data.title'
        },

        enabled: isArticleTopicPage && !!articleTopic
    })

    const articleCrumbs = useMemo(() => {
        const crumbs = []
        if ((articleTopicDataLoading && !!articleTopic) || !pageData) return crumbs
        switch (pageType) {
            case learningCenterArticlePageTypes.ARTICLE:
                {
                    const {title} = pageData
                    if (title) {
                        const crumbContent = generateBreadcrumbLinkContent({
                            title,
                            isClickable: false
                        })
                        crumbs.push(articlesPageData(), crumbContent)
                        return addStaticCrumbs(crumbs)
                    }
                }
                break
            case learningCenterArticlePageTypes.ARTICLE_TOPIC: {
                const {title} = articleTopicData?.data || {}
                if (title) {
                    const crumbContent = generateBreadcrumbLinkContent({
                        title,
                        isClickable: false
                    })
                    crumbs.push(articlesPageData(), crumbContent)
                    return addStaticCrumbs(crumbs)
                } else {
                    crumbs.push(articlesPageData(false))
                    return addStaticCrumbs(crumbs)
                }
            }
        }
        return crumbs
    }, [pageData, pageType, articleTopicData, articleTopicDataLoading, articleTopic])

    if (articleTopicDataError) {
        throw new Error(
            'An error has occurred while fetching Article Topic data for the Learning Center Article Breadcrumb component:',
            articleTopicDataError
        )
    }

    return (
        <BuilderBreadcrumb
            crumbs={articleCrumbs}
            builderBlock={builderBlock}
            attributes={attributes}
            separator={separator}
            data-testid="learningCenterArticleBreadcrumb-BuilderBreadcrumb"
        />
    )
}

LearningCenterArticleBreadcrumb.propTypes = {
    pageType: PropTypes.string,
    attributes: PropTypes.object,
    separator: PropTypes.node,
    builderBlock: PropTypes.object
}

export const LearningCenterBreadcrumbDefinition = {
    component: LearningCenterArticleBreadcrumb,
    name: 'LearningCenterArticleBreadcrumb',
    noWrap: true,
    shouldReceiveBuilderProps: {
        builderBlock: true
    },
    inputs: [
        {
            name: 'separator',
            type: 'string',
            defaultValue: '>',
            friendlyName: 'Breadcrumb Separator'
        }
    ]
}

export const LearningCenterArticleBreadcrumbDefinition = {
    component: LearningCenterArticleBreadcrumb,
    name: 'LearningCenterArticleBreadcrumb',
    noWrap: true,
    shouldReceiveBuilderProps: {
        builderBlock: true
    },
    inputs: [
        {
            name: 'separator',
            type: 'string',
            defaultValue: '>',
            friendlyName: 'Breadcrumb Separator'
        }
    ]
}
